import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import api2 from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, FileSpreadsheet, FileText, ClipboardCheck, ClipboardList, PackageCheck, PlayCircle, Briefcase } from "lucide-react";
import Pagination, { usePagination } from "@/components/Pagination";
import ImportButton from "@/components/ImportButton";

const RANGES = ["Today", "Yesterday", "Last 7 Days", "Last 30 Days", "Last 90 Days", "This Month", "Custom"];

function rangeToDates(rng) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const start = new Date(today);
  switch (rng) {
    case "Today":         return { from: iso(today), to: iso(today) };
    case "Yesterday":     start.setDate(today.getDate() - 1); return { from: iso(start), to: iso(start) };
    case "Last 7 Days":   start.setDate(today.getDate() - 6); return { from: iso(start), to: iso(today) };
    case "Last 30 Days":  start.setDate(today.getDate() - 29); return { from: iso(start), to: iso(today) };
    case "Last 90 Days":  start.setDate(today.getDate() - 89); return { from: iso(start), to: iso(today) };
    case "This Month":    return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
    default:              return null;
  }
}

export default function ServiceReport() {
  const { user: currentUser } = useAuth();
  const isBranch = currentUser?.role === "branch";
  const today = new Date().toISOString().slice(0, 10);
  const past = new Date(); past.setDate(past.getDate() - 29);
  const [rng, setRng] = useState("Last 30 Days");
  const [dateFrom, setDateFrom] = useState(past.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("all");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total: 0, pending: 0, in_progress: 0, completed: 0, delivered: 0 });
  const [loading, setLoading] = useState(false);
  const paginationKey = `${rng}|${dateFrom}|${dateTo}|${branchId}`;
  const { page, setPage, pageRows, totalPages, total, pageSize, setPageSize } = usePagination(rows, 50, paginationKey);

  useEffect(() => {
    const d = rangeToDates(rng);
    if (d) { setDateFrom(d.from); setDateTo(d.to); }
  }, [rng]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/service-summary", {
        params: { rng, date_from: dateFrom, date_to: dateTo, branch_id: branchId },
      });
      setRows(data?.rows || []);
      setTotals(data?.totals || { total: 0, pending: 0, in_progress: 0, completed: 0, delivered: 0 });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!isBranch) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    load();
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, branchId]);

  const downloadFile = async (format) => {
    const path = format === "pdf" ? "/export-pdf/service-summary" : "/export/service-summary";
    const res = await api2.get(path, { params: { rng, date_from: dateFrom, date_to: dateTo, branch_id: branchId }, responseType: "blob" });
    const ext = format === "pdf" ? "pdf" : "xlsx";
    const mime = format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
    const a = document.createElement("a");
    a.href = url; a.download = `service-report-${dateFrom}_to_${dateTo}.${ext}`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadAll = async () => {
    const res = await api2.get(`/export/service-summary?all=true`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `service-report-ALL.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="service-report-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight text-slate-900">Service Report</h1>
          <p className="text-xs text-slate-500 mt-0.5">Branch-wise service performance — total, pending, in-progress, completed, delivered.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => downloadFile("xlsx")} className="h-8 bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white text-xs font-semibold" data-testid="svc-excel-btn">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel
          </Button>
          <Button onClick={() => downloadFile("pdf")} className="h-8 bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold" data-testid="svc-pdf-btn">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
          </Button>
          {currentUser?.role === "admin" && (
            <>
              <Button onClick={downloadAll} className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold" data-testid="svc-excel-all-btn" title="Admin: download every historical service record">
                <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Export ALL
              </Button>
              <ImportButton resource="receipts" onDone={load} label="Import Excel" />
            </>
          )}
          <Button onClick={load} disabled={loading} className="h-8 bg-[#2E77E6] hover:bg-[#2361C4] text-white text-xs font-semibold" data-testid="svc-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-xs">
          <span className="uppercase tracking-widest font-bold text-slate-500">Range</span>
          <Select value={rng} onValueChange={setRng}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="svc-rng-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="uppercase tracking-widest font-bold text-slate-500">From</span>
          <Input type="date" value={dateFrom} onChange={(e) => { setRng("Custom"); setDateFrom(e.target.value); }} className="h-8 w-36 text-xs" data-testid="svc-date-from" />
          <span className="uppercase tracking-widest font-bold text-slate-500">To</span>
          <Input type="date" value={dateTo} onChange={(e) => { setRng("Custom"); setDateTo(e.target.value); }} className="h-8 w-36 text-xs" data-testid="svc-date-to" />
        </div>
        {!isBranch && (
          <div className="flex items-center gap-2 text-xs">
            <span className="uppercase tracking-widest font-bold text-slate-500">Branch</span>
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="h-8 w-48 text-xs" data-testid="svc-branch-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Total", totals.total, Briefcase, "#0B2E5C"],
          ["Pending", totals.pending, ClipboardList, "#E8A93A"],
          ["In Progress", totals.in_progress, PlayCircle, "#2E77E6"],
          ["Completed", totals.completed, ClipboardCheck, "#2AA36F"],
          ["Delivered", totals.delivered, PackageCheck, "#186A65"],
        ].map(([label, value, Icon, color]) => (
          <div key={label} className="rounded-md bg-white border border-slate-200 flex shadow-sm">
            <div className="w-12 shrink-0 flex items-center justify-center" style={{ background: color }}>
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
              <div className="font-display font-black text-2xl tracking-tight text-slate-900 font-mono-num mt-0.5">{value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm crm-table" data-testid="service-report-table">
            <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold">Branch</th>
                <th className="px-3 py-2.5 text-left font-semibold">Service Name</th>
                <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                <th className="px-3 py-2.5 text-right font-semibold">Pending</th>
                <th className="px-3 py-2.5 text-right font-semibold">In Progress</th>
                <th className="px-3 py-2.5 text-right font-semibold">Completed</th>
                <th className="px-3 py-2.5 text-right font-semibold">Delivered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">No service activity in the selected range.</td></tr>
              ) : pageRows.map((r, i) => (
                <tr key={r.branch_name + "|" + r.service_name + i}>
                  <td className="px-3 py-2 text-slate-600">{r.branch_name}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">{r.service_name}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-bold">{r.total}</td>
                  <td className={`px-3 py-2 text-right font-mono-num ${r.pending ? "text-amber-700 font-bold" : "text-slate-400"}`}>{r.pending}</td>
                  <td className={`px-3 py-2 text-right font-mono-num ${r.in_progress ? "text-blue-700 font-bold" : "text-slate-400"}`}>{r.in_progress}</td>
                  <td className={`px-3 py-2 text-right font-mono-num ${r.completed ? "text-emerald-700 font-bold" : "text-slate-400"}`}>{r.completed}</td>
                  <td className={`px-3 py-2 text-right font-mono-num ${r.delivered ? "text-[color:var(--brand-teal)] font-bold" : "text-slate-400"}`}>{r.delivered}</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="bg-[color:var(--brand-navy)] text-white font-semibold" data-testid="svc-totals-row">
                  <td colSpan={2} className="px-3 py-3 text-right uppercase tracking-widest text-[11px]">Totals</td>
                  <td className="px-3 py-3 text-right font-mono-num">{totals.total}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-amber-300">{totals.pending}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-blue-300">{totals.in_progress}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-emerald-300">{totals.completed}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-white">{totals.delivered}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} setPageSize={setPageSize} testid="service-report-pagination" />
      </div>
    </div>
  );
}
